/**
 * Represents a row of CSV data where each key-value pair corresponds to a column.
 * Values can be strings, numbers, booleans, or null.
 *
 * @example
 * const data = [
 *   { name: "John", age: 30 },
 *   { name: "Jane", age: 25 }
 * ];
 * downloadCSV(data, "users.csv");
 */
export interface CSVRow {
  [key: string]: string | number | boolean | null;
}

/**
 * Downloads data as a CSV file in the browser.
 *
 * @param rows - Array of objects representing CSV rows. Each object's keys become column headers.
 * @param filename - Name of the file to be downloaded (should include .csv extension)
 * It is assumed that the column headers are the keys of the first row in the array.
 *
 * @example
 * const data = [
 *   { name: "John", age: 30 },
 *   { name: "Jane", age: 25 }
 * ];
 * File with the following content will be downloaded to the user's computer:
 *
 * name, age
 * John, 30
 * Jane, 25
 */
export const downloadCSV = (rows: CSVRow[], filename: string): void => {
  const csvContent = convertRowsToCSVContent(rows);
  // Add BOM for Excel UTF-8 compatibility
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], {
    type: 'text/csv;charset=utf-8;',
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';

  // Trigger download
  if (document.createEvent) {
    const event = document.createEvent('MouseEvents');
    event.initEvent('click', true, true);
    link.dispatchEvent(event);
  } else {
    link.click();
  }

  // Clean up
  URL.revokeObjectURL(url);
};

/**
 * Converts an array of objects into a CSV string.
 *
 * @param rows - Array of objects representing CSV rows
 * @returns A string in CSV format with headers and data rows
 */
const convertRowsToCSVContent = (rows: CSVRow[]): string => {
  if (rows.length === 0) return '';

  // Get headers from the first row
  const headers = Object.keys(rows[0]);

  // Create CSV header row
  const headerRow = headers.join(',');

  // Convert each data row to CSV format
  const dataRows = rows.map((row) => {
    return headers
      .map((header) => {
        const value = row[header];
        // Handle different value types and escape special characters
        if (value === null) return '';
        if (typeof value === 'string') {
          // Escape quotes and wrap in quotes if contains comma or quotes
          const escaped = value.replace(/"/g, '""');
          return escaped.includes(',') || escaped.includes('"')
            ? `"${escaped}"`
            : escaped;
        }
        return String(value);
      })
      .join(',');
  });

  // Combine header and data rows with newlines
  return [headerRow, ...dataRows].join('\n');
};
